import React, { useRef, useState } from 'react';
import { Paperclip, X, FileText, Image as ImageIcon, File as FileIcon } from 'lucide-react';
import { apiFetch, ApiError } from '../../lib/api';

const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function iconFor(contentType = '') {
  if (contentType.startsWith('image/')) return ImageIcon;
  if (contentType.includes('pdf') || contentType.startsWith('text/')) return FileText;
  return FileIcon;
}

export default function FileUploadField({ attachments, onChange, disabled, scope = 'tickets', uploadPath = '/api/upload' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const remaining = MAX_FILES - (attachments?.length || 0);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setError('');

    if (files.length > remaining) {
      setError(`Only ${remaining} more file${remaining === 1 ? '' : 's'} allowed`);
      return;
    }
    for (const f of files) {
      if (f.size > MAX_FILE_BYTES) {
        setError(`"${f.name}" exceeds 10 MB`);
        return;
      }
    }

    const formData = new FormData();
    formData.append('scope', scope);
    for (const f of files) formData.append('files', f);

    setUploading(true);
    try {
      const data = await apiFetch(uploadPath, { method: 'POST', body: formData });
      const newItems = Array.isArray(data.attachments) ? data.attachments : [];
      onChange([...(attachments || []), ...newItems]);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Upload failed');
      } else {
        setError('Upload failed, try again');
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (idx) => {
    const next = attachments.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {(attachments || []).map((a, idx) => {
          const Icon = iconFor(a.contentType);
          return (
            <span
              key={a.key}
              className="inline-flex items-center gap-2 pl-2.5 pr-1.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[12px] text-gray-200 max-w-full"
            >
              <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" />
              <span className="truncate max-w-[220px]" title={a.filename}>{a.filename}</span>
              <span className="text-gray-600">{formatBytes(a.size)}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="p-0.5 rounded-full text-gray-500 hover:text-white hover:bg-white/[0.08] transition-colors"
                  aria-label={`Remove ${a.filename}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          );
        })}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading || remaining <= 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-white/[0.12] text-[12px] text-gray-400 hover:text-white hover:border-white/[0.25] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <>
              <span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Paperclip className="w-3 h-3" />
              {(attachments?.length || 0) === 0 ? 'Attach files' : 'Add more'}
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        />
      </div>
      <p className="mt-2 text-[11px] text-gray-600">
        Up to {MAX_FILES} files, 10 MB each. Images, PDFs, docs, spreadsheets.
      </p>
      {error && (
        <p className="mt-2 text-[11px] text-red-400">{error}</p>
      )}
    </div>
  );
}
